import { StanInput, StanModal } from '@/components';
import { autoAddHttps } from '@/lib';
import { Editor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import IconLink from '@/assets/icons/ic-link.svg';
import IconPencil from '@/assets/icons/ic-pencil.svg';

export const LinkUploadModal = ({
    isOpen,
    onClose,
    editor,
}: {
    isOpen: boolean;
    onClose: () => void;
    editor: Editor;
}) => {
    const [text, setText] = useState('');
    const [link, setLink] = useState('');

    // Initialize modal with selected text and existing link if available
    useEffect(() => {
        if (isOpen) {
            const { from, to } = editor.state.selection;
            const hasSelection = from !== to;

            if (hasSelection) {
                const selectedText = editor.state.doc.textBetween(from, to);
                setText(selectedText);

                // Check if the selection already has a link
                const linkMark = editor.getAttributes('link');
                if (linkMark.href) {
                    setLink(linkMark.href);
                }
            } else {
                // Clear fields when no selection
                setText('');
                setLink('');
            }
        }
    }, [isOpen, editor]);

    const handleClose = () => {
        setText('');
        setLink('');
        onClose();
    };

    const handleSave = ({ text, link }: { text: string; link: string }) => {
        if (!link.trim()) return;

        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;

        if (hasSelection) {
            // If text is selected and the text field is not empty, replace the selection with the new text and apply the link
            if (text.trim() && text !== editor.state.doc.textBetween(from, to)) {
                editor
                    .chain()
                    .focus()
                    .insertContentAt({ from, to }, text)
                    .extendMarkRange('link')
                    .setLink({ href: autoAddHttps(link), target: '_blank' })
                    .run();
            } else {
                // If text is selected and text field is empty or unchanged, just apply the link to the selection
                editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .setLink({ href: autoAddHttps(link), target: '_blank' })
                    .run();
            }
        } else {
            // If no text is selected, insert the text and apply the link
            const linkText = text.trim() || link;
            editor
                .chain()
                .focus()
                .insertContent(`<a href="${autoAddHttps(link)}" target="_blank">${linkText}</a>`)
                .run();
        }

        handleClose();
    };

    const handleRemoveHyperlink = () => {
        editor.chain().focus().unsetLink().run();
        handleClose();
    };

    return (
        <StanModal
            id='link-upload-modal'
            isOpen={isOpen}
            onClose={handleClose}
            title='Insert Link'
            primaryActionLabel='Save'
            onPrimaryAction={() => handleSave({ text, link })}
            secondaryActionLabel='Remove Hyperlink'
            onSecondaryAction={handleRemoveHyperlink}
            size='xs'
        >
            <div className='flex flex-col gap-2'>
                <StanInput
                    innerLabel='Link Text'
                    name='text'
                    type='text'
                    value={text}
                    onChange={e => setText(e.target.value)}
                    startIcon={IconPencil}
                    placeholder='Enter display text for the link'
                />
                <StanInput
                    innerLabel='Link URL'
                    name='link'
                    type='text'
                    value={link}
                    startIcon={IconLink}
                    onChange={e => setLink(e.target.value)}
                    placeholder='https://example.com'
                />
            </div>
        </StanModal>
    );
};
